import React from 'react';
import PropTypes from 'prop-types';
import { Box, ButtonBase, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Check, PriorityHigh } from '@mui/icons-material';
import { statusColors, FONT } from '../styles/wizardSx';

/**
 * Vertical step rail for the automation wizard.
 *
 * Plain props, no knowledge of the automation state: the wizard decides each
 * step's status and whether it can be reached, the rail only draws it.
 *   status:   'current' | 'complete' | 'error' | 'upcoming'
 *   reachable[i]: false renders the step as disabled and ignores clicks.
 */

const STATUS_TEXT = {
  current: 'In progress',
  complete: 'Complete',
  error: 'Needs attention',
  upcoming: 'Not started',
};

function StepMarker({ index, status, icon }) {
  return (
    <Box
      sx={(theme) => {
        const base = {
          width: 36,
          height: 36,
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          border: '2px solid',
          fontWeight: 600,
          fontSize: FONT.body,
          transition: 'background-color 0.2s ease, border-color 0.2s ease',
          '& svg': { fontSize: 20 },
        };
        if (status === 'current') {
          return { ...base, bgcolor: 'primary.main', borderColor: 'primary.main', color: 'primary.contrastText' };
        }
        if (status === 'complete') {
          const { bg, text } = statusColors(theme, 'success');
          return { ...base, bgcolor: bg, borderColor: bg, color: text };
        }
        if (status === 'error') {
          const { bg, text } = statusColors(theme, 'error');
          return { ...base, bgcolor: bg, borderColor: text, color: text };
        }
        return { ...base, bgcolor: 'background.paper', borderColor: 'outline.variant', color: 'text.secondary' };
      }}
    >
      {status === 'complete' && <Check />}
      {status === 'error' && <PriorityHigh />}
      {(status === 'current' || status === 'upcoming') && (icon || index + 1)}
    </Box>
  );
}

StepMarker.propTypes = {
  index: PropTypes.number.isRequired,
  status: PropTypes.oneOf(['current', 'complete', 'error', 'upcoming']).isRequired,
  icon: PropTypes.node,
};

function WizardStepper({ steps, activeStep, statuses, reachable, onStepClick }) {
  return (
    <Box component="nav" aria-label="Automation steps">
      <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {steps.map((step, index) => {
          const status = statuses[index] || 'upcoming';
          const isActive = index === activeStep;
          const canClick = !isActive && Boolean(reachable[index]);
          const isLast = index === steps.length - 1;

          return (
            <Box component="li" key={step.label} sx={{ position: 'relative' }}>
              {!isLast && (
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    left: 29,
                    top: 50,
                    bottom: -6,
                    width: '2px',
                    borderRadius: '1px',
                    bgcolor: status === 'complete' ? 'success.main' : 'outline.variant',
                  }}
                />
              )}
              <ButtonBase
                onClick={canClick ? () => onStepClick(index) : undefined}
                disabled={!isActive && !canClick}
                aria-current={isActive ? 'step' : undefined}
                aria-label={`Step ${index + 1}: ${step.label}, ${STATUS_TEXT[status]}`}
                sx={(theme) => ({
                  width: '100%',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  gap: 1.5,
                  px: 1.5,
                  py: 1.25,
                  mb: 1.5,
                  borderRadius: '12px',
                  cursor: canClick ? 'pointer' : 'default',
                  bgcolor: isActive ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08) : 'transparent',
                  transition: 'background-color 0.2s ease',
                  '&:hover': canClick ? { bgcolor: 'surface.container' } : {},
                  '&.Mui-focusVisible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 1,
                  },
                  '&.Mui-disabled': { opacity: 1 },
                })}
              >
                <StepMarker index={index} status={status} icon={step.icon} />
                <Box sx={{ minWidth: 0, pt: 0.25 }}>
                  <Typography
                    sx={{
                      fontSize: FONT.body,
                      fontWeight: isActive ? 700 : 600,
                      color: !isActive && !canClick ? 'text.secondary' : 'text.primary',
                      lineHeight: 1.4,
                    }}
                  >
                    {step.label}
                  </Typography>
                  <Typography
                    sx={(theme) => ({
                      fontSize: FONT.caption,
                      lineHeight: 1.4,
                      color:
                        status === 'error'
                          ? statusColors(theme, 'error').text
                          : 'text.secondary',
                    })}
                  >
                    {status === 'error' ? STATUS_TEXT.error : step.description}
                  </Typography>
                </Box>
              </ButtonBase>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

WizardStepper.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
      icon: PropTypes.node,
    })
  ).isRequired,
  activeStep: PropTypes.number.isRequired,
  statuses: PropTypes.arrayOf(PropTypes.string).isRequired,
  reachable: PropTypes.arrayOf(PropTypes.bool).isRequired,
  onStepClick: PropTypes.func.isRequired,
};

export default WizardStepper;
